import type { Id } from '@odhaczka/core';
import { useLiveQuery } from 'dexie-react-hooks';
import { commit, type Writer } from '../db/commit';
import { db } from '../db/db';
import type { ArticleRow, ListItemRow, ReceiptLineRow, ReceiptRow } from '../db/types';

// Paragony na telefonie: zapisuje je serwer (łącznik Lidl Plus), tu tylko odczyt i decyzje użytkownika
// o dopasowaniu linii do pozycji listy (potwierdzenie, odrzucenie, ręczne przypisanie).

export function useReceipts(): ReceiptRow[] | undefined {
  return useLiveQuery(async () =>
    (await db.receipts.toArray()).filter((r) => !r.deleted).sort((a, b) => (b.purchasedMs ?? 0) - (a.purchasedMs ?? 0)),
  );
}

export function useReceipt(id: Id | undefined): ReceiptRow | null | undefined {
  return useLiveQuery(async () => (id ? ((await db.receipts.get(id)) ?? null) : null), [id]);
}

export interface ReceiptLineView extends ReceiptLineRow {
  article: ArticleRow | null;
}

export function useReceiptLines(receiptId: Id | undefined): ReceiptLineView[] | undefined {
  return useLiveQuery(async () => {
    if (!receiptId) return [];
    const lines = (await db.receiptLines.where('receiptId').equals(receiptId).toArray()).filter((l) => !l.deleted).sort((a, b) => a.idx - b.idx);
    const ids = [...new Set(lines.map((l) => l.articleId).filter((a): a is Id => !!a))];
    const articles = new Map((await db.articles.bulkGet(ids)).filter((a): a is ArticleRow => !!a).map((a) => [a.id, a]));
    return lines.map((l) => ({ ...l, article: l.articleId ? (articles.get(l.articleId) ?? null) : null }));
  }, [receiptId]);
}

/** Paragony przypisane do listy (zwykle jeden). */
export function useListReceipts(listId: Id | undefined): ReceiptRow[] | undefined {
  return useLiveQuery(
    async () =>
      listId
        ? (await db.receipts.where('listId').equals(listId).toArray()).filter((r) => !r.deleted).sort((a, b) => (b.purchasedMs ?? 0) - (a.purchasedMs ?? 0))
        : [],
    [listId],
  );
}

/** Linie paragonów przypisane do pozycji listy (ceny zapłacone za pozycję). */
export function useListLines(listId: Id | undefined): Map<Id, ReceiptLineRow[]> | undefined {
  return useLiveQuery(async () => {
    const out = new Map<Id, ReceiptLineRow[]>();
    if (!listId) return out;
    const receipts = (await db.receipts.where('listId').equals(listId).toArray()).filter((r) => !r.deleted);
    for (const r of receipts) {
      for (const l of await db.receiptLines.where('receiptId').equals(r.id).toArray()) {
        if (l.deleted || !l.listItemId || l.match === 'rejected' || l.match === 'suggested') continue;
        out.set(l.listItemId, [...(out.get(l.listItemId) ?? []), l]);
      }
    }
    return out;
  }, [listId]);
}

/** Wartość linii po rabatach. */
export const linePaidGr = (l: Pick<ReceiptLineRow, 'valueGr' | 'discountGr'>) => (l.valueGr ?? 0) - (l.discountGr ?? 0);

/** Pozycja z potwierdzonym zakupem: w koszyku (jeśli czekała albo była brakiem), artykuł uczy się produktu. */
async function applyPurchase(w: Writer, line: ReceiptLineRow, item: ListItemRow, receipt: ReceiptRow | undefined) {
  const article = line.articleId ? await db.articles.get(line.articleId) : undefined;
  if (article && !article.productId && item.productId) await w.set('articles', article.id, { productId: item.productId, linkSource: 'user' });
  if (item.status === 'pending' || item.status === 'missing') {
    await w.set('listItems', item.id, { status: 'cart', checkedMs: item.checkedMs ?? receipt?.purchasedMs ?? Date.now() });
  }
}

/** Potwierdza propozycję albo przypisuje linię do pozycji listy. */
export async function assignLine(lineId: Id, itemId: Id): Promise<void> {
  await commit(async (w) => {
    const line = await db.receiptLines.get(lineId);
    const item = await db.listItems.get(itemId);
    if (!line || !item || item.deleted) throw new Error('Nie ma takiej linii albo pozycji');
    await w.set('receiptLines', lineId, { listItemId: itemId, match: 'confirmed' });
    await applyPurchase(w, line, item, await db.receipts.get(line.receiptId));
  });
}

/** Linia nie należy do żadnej pozycji listy (odrzucona propozycja albo błędne dopasowanie). */
export async function rejectLine(lineId: Id): Promise<void> {
  await commit((w) => w.set('receiptLines', lineId, { listItemId: null, match: 'rejected' }));
}
